import { z } from 'zod';

import { defineRoute } from '../http/route-contract';
import { IsoDateTimeSchema } from '../primitives/datetime';
import { TraderIdSchema } from '../primitives/ids';

/** Body of POST /auth/telegram (master X1). */
export const AuthTelegramRequestSchema = z.strictObject({
  initData: z
    .string()
    .min(1)
    .max(4096)
    .describe(
      'Raw Telegram WebApp initData. Verified server side (HMAC, auth_date ≤ 24 h); never logged (I9).',
    ),
});
/** POST /auth/telegram body. */
export type AuthTelegramRequest = z.infer<typeof AuthTelegramRequestSchema>;

/** The affiliate as the trader may see it: name only (I8). */
export const AffiliateSummarySchema = z.object({ displayName: z.string().min(1).max(128) });
/** Affiliate summary. */
export type AffiliateSummary = z.infer<typeof AffiliateSummarySchema>;

/** Response of POST /auth/telegram. The Mini App keeps the token in memory only (technical spec §4). */
export const AuthTelegramResponseSchema = z.object({
  accessToken: z
    .string()
    .min(1)
    .describe(
      'Opaque bearer token, valid 15 min (master X1). Renewed by calling this route again.',
    ),
  expiresAt: IsoDateTimeSchema,
  traderId: TraderIdSchema,
  isNew: z
    .boolean()
    .describe(
      'True when this call created the trader (first opening). S2 AC1/AC2, S3 R5. Provisional.',
    ),
  affiliate: AffiliateSummarySchema.nullable().describe(
    'Affiliate locked on the first visit, or null without a link (FXM-01 §4, §6 case 9).',
  ),
});
/** POST /auth/telegram response. */
export type AuthTelegramResponse = z.infer<typeof AuthTelegramResponseSchema>;

/** POST /auth/telegram: the only route without a bearer token. */
export const authTelegramRoute = defineRoute({
  operationId: 'authTelegram',
  method: 'POST',
  path: '/auth/telegram',
  tag: 'Auth',
  summary: 'Exchange Telegram initData for an access token',
  description:
    'Verifies initData, creates the trader on the first visit and locks the affiliate (the first link wins). ' +
    'Also renews the 15-minute token.',
  source: 'technical spec §6; system design §API; master X1; US-01, US-02',
  auth: 'none',
  provisional: {
    questions: ['Q22', 'Q75'],
    note: 'the affiliate comes from start_param or from the bot /start; the affiliate directory is open.',
  },
  // C1 spec R7: no trader exists yet to scope a key, and the call is naturally idempotent.
  idempotencyExemption:
    'No trader exists yet to scope a key; the same initData yields the same trader and the first link wins ' +
    '(FXM-01 §6 case 9). Master X3 (exemption decided).',
  body: { name: 'AuthTelegramRequest', schema: AuthTelegramRequestSchema },
  success: {
    kind: 'json',
    status: 200,
    body: { name: 'AuthTelegramResponse', schema: AuthTelegramResponseSchema },
  },
  errors: [
    { status: 401, reasonCode: 'INIT_DATA_INVALID' },
    { status: 401, reasonCode: 'INIT_DATA_EXPIRED' },
    // S2 R17: rate limited per client IP.
    { status: 429, reasonCode: 'RATE_LIMITED' },
    // S2 R5, spec §5.1: the affiliate directory failed (Q75). Declared by the API (PR #4).
    { status: 503, reasonCode: 'SERVICE_UNAVAILABLE' },
  ],
});
